import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { CircleAlert, Copy, Loader2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import {
  FOGBUGZ_CODE_MAX_FOLLOW_UPS,
  FOGBUGZ_CODE_QUESTION_MAX_CHARS,
  FOGBUGZ_CODE_RUN_PHASE_LABELS,
  fogbugzCodeProgressEventSchema,
  hasAllCodeReportSections,
  type FogbugzCodeErrorCode,
  type FogbugzCodeOperation,
  type FogbugzCodeProgressEvent,
  type FogbugzCodeRef,
  type FogbugzCodeRefsResult,
  type FogbugzToolsetBridge,
} from "@ttc-spectree/toolbox-contract";
import {
  DEFAULT_TOOLBOX_PERSONA,
  TOOLBOX_PERSONA_LABELS,
  type ToolboxPersona,
} from "@ttc-spectree/toolbox-contract/roles";
import { MarkdownRenderer } from "@/components/common/markdown-renderer";
import { Chip } from "@/components/grooming/grooming-chips";
import { Button } from "@/components/ui/button";
import { useGroomingProjects } from "@/hooks/queries/use-grooming";
import { personasOn, projectsWith, useToolboxAccess } from "@/hooks/queries/use-toolbox-access";
import type { ToolboxAccessResponse } from "@/lib/api/toolbox-access";
import { readLastProject } from "@/lib/grooming/last-project";
import { useClaudeRunOutcome } from "@/lib/toolbox/claude-signals";
import { cn } from "@/lib/utils";

/**
 * "Look into the code" under the FogBugz event log summary review (epic
 * PI-SPEC-SW-E307, feature PI-SPEC-SW-1604). On demand only: the person's
 * own Claude Code reads the chosen Toolbox project's repository copies on
 * this computer, at the branch or tag the person picks (`main` by default),
 * and writes a three-part report (where it failed, a code review of the
 * fault and its callers, and the change history of both) for the person's
 * Toolbox persona on that project. Up to `FOGBUGZ_CODE_MAX_FOLLOW_UPS`
 * follow-up questions continue the same thread.
 *
 * App only, by owner decision: nothing from this section is ever posted to
 * the case. It never touches the reviewed-post request; the page posts only
 * the summary sections it already holds. Claude stays read-only (the engine
 * runs git and hands Claude the history).
 *
 * The contract has no "close thread" operation: a new `investigateCode`
 * ends the previous thread in the engine, and an idle thread expires after
 * `FOGBUGZ_CODE_THREAD_IDLE_MS`. Leaving the case, changing the project or
 * version, or looking again drops the thread here; a run still going when
 * the section closes is stopped with `cancel`.
 */

// ---------------------------------------------------------------------------
// Pure helpers (exported for tests)
// ---------------------------------------------------------------------------

/** A plain message for a code operation's refusal, whether Setup is where to fix it, and whether to offer "Look into the code again". */
export interface FogbugzCodeMessage {
  text: string;
  setup: boolean;
  restart: boolean;
}

type MessageWords = string | Readonly<Record<FogbugzCodeOperation, string>>;

const CODE_MESSAGES: Readonly<Record<FogbugzCodeErrorCode, { words: MessageWords; setup: boolean; restart?: boolean }>> = {
  CLAUDE_NOT_FOUND: { words: "Claude Code is not installed on this computer, so Claude could not look into the code.", setup: true },
  CLAUDE_NOT_SIGNED_IN: { words: "Claude Code is not signed in on this computer. Sign in, then try again.", setup: true },
  API_KEY_BILLING: {
    words:
      "Claude Code on this computer would bill an API key instead of your Claude subscription, so SpecTree Toolbox did not run it.",
    setup: true,
  },
  USAGE_LIMIT: { words: "Your Claude usage limit was reached. Try again once it resets.", setup: false },
  TIMED_OUT: {
    words: {
      investigate: "Claude took too long looking into the code, so it was stopped. Try again.",
      ask: "Claude took too long to answer, so it was stopped. Ask again.",
    },
    setup: false,
  },
  PLUGIN_MISSING: {
    words: "The SpecTree plugin for Claude Code is missing on this computer, so Claude cannot look into the code.",
    setup: true,
  },
  NO_SUMMARY_RETURNED: {
    words: {
      investigate: "Claude finished without a complete report (all three parts). Try again.",
      ask: "Claude finished without an answer. Ask again.",
    },
    setup: false,
  },
  CANCELED: {
    words: { investigate: "You stopped Claude looking into the code.", ask: "You stopped the answer." },
    setup: false,
  },
  BUSY: {
    words: "SpecTree Toolbox is already running Claude for a FogBugz summary or the code. Wait for it to finish, then try again.",
    setup: false,
  },
  REFUSED: {
    words:
      "SpecTree Toolbox stopped this before Claude started, because it could not keep the run read-only and private on this computer.",
    setup: true,
  },
  NETWORK: { words: "Claude Code could not reach Anthropic. Check the network connection, then try again.", setup: false },
  UNKNOWN: {
    words: {
      investigate: "Claude Code could not finish looking into the code. Try again.",
      ask: "Claude Code could not answer this question. Ask again.",
    },
    setup: false,
  },
  CODE_NOT_SET_UP: { words: "This project's code isn't set up on this computer.", setup: true },
  REF_NOT_FOUND: {
    words: "That branch or tag is not in this project's code on this computer. Choose another version.",
    setup: false,
  },
  THREAD_EXPIRED: {
    words:
      "This conversation about the code has ended: it was idle too long, or SpecTree Toolbox restarted. Look into the code again to ask more.",
    setup: false,
    restart: true,
  },
  TOO_MANY_FOLLOW_UPS: {
    words: `You asked the ${String(FOGBUGZ_CODE_MAX_FOLLOW_UPS)} follow-up questions one conversation allows. Look into the code again to ask more.`,
    setup: false,
    restart: true,
  },
};

/**
 * Plain words for a "Look into the code" refusal. For a usage limit the
 * engine's own message (which names the reset time) is added; for REFUSED
 * and CLAUDE_NOT_FOUND the engine's sentence (what to fix, such as an update
 * to a newer Claude Code) replaces the generic one. Nothing here
 * is ever posted, so no message talks about the case. Pure; exported for tests.
 */
export function fogbugzCodeMessage(
  code: FogbugzCodeErrorCode,
  operation: FogbugzCodeOperation = "investigate",
  engineMessage?: string,
): FogbugzCodeMessage {
  const base = CODE_MESSAGES[code];
  const detail = engineMessage?.trim();
  let text = typeof base.words === "string" ? base.words : base.words[operation];
  if (code === "USAGE_LIMIT" && detail) text = `${text} ${detail}`;
  if ((code === "REFUSED" || code === "CLAUDE_NOT_FOUND") && detail) text = detail;
  return { text, setup: base.setup, restart: base.restart ?? false };
}

/** The versions could not be read because the project's code copies are being updated; the section asks again by itself. */
export const CODE_REFS_BUSY_WORDS = "SpecTree Toolbox is updating this project's code. The versions will show in a moment.";

/** How often the section asks for the versions again while the code copies are busy. */
export const CODE_REFS_BUSY_RETRY_MS = 10_000;

/** A ref as a select value: `branch:main`, `tag:v2.13.1`. Pure; exported for tests. */
export function codeRefKey(ref: FogbugzCodeRef): string {
  return `${ref.kind}:${ref.name}`;
}

/** A ref in plain words: `main` for a branch, `v2.13.1 (release tag)` for a tag. Pure; exported for tests. */
export function codeRefWords(ref: FogbugzCodeRef): string {
  return ref.kind === "tag" ? `${ref.name} (release tag)` : ref.name;
}

/**
 * The versions the picker offers: the default first, then the other
 * branches, then the tags, once each, in the engine's order. Pure; exported
 * for tests.
 */
export function codeRefOptions(refs: readonly FogbugzCodeRef[], defaultRef: FogbugzCodeRef): FogbugzCodeRef[] {
  const seen = new Set<string>([codeRefKey(defaultRef)]);
  const out: FogbugzCodeRef[] = [defaultRef];
  for (const kind of ["branch", "tag"] as const) {
    for (const ref of refs) {
      if (ref.kind !== kind || seen.has(codeRefKey(ref))) continue;
      seen.add(codeRefKey(ref));
      out.push(ref);
    }
  }
  return out;
}

/**
 * The projects whose code the person may look into: the projects their
 * Toolbox roles there include the FogBugz tools on, and for an Admin every
 * Toolbox project. Pure; exported for tests.
 */
export function codeProjectsOf(
  access: ToolboxAccessResponse | null | undefined,
  adminProjects: readonly string[] | undefined,
): string[] {
  if (!access) return [];
  const names = [...projectsWith(access, "fogbugz"), ...(access.isAdmin ? (adminProjects ?? []) : [])];
  const seen = new Set<string>();
  return names.filter((name) => {
    const key = name.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * The project the section starts on: the person's current Toolbox project
 * (the last project shown on this computer, as on Toolbox home and in the
 * title bar) when it is one of `projects`, else the first. Pure; exported for tests.
 */
export function defaultCodeProject(projects: readonly string[], current: string | undefined): string | null {
  if (current) {
    const match = projects.find((name) => name.toLowerCase() === current.toLowerCase());
    if (match) return match;
  }
  return projects[0] ?? null;
}

/** The persona the report is written for on `project`: the person's Toolbox persona there. Pure; exported for tests. */
export function codePersonaFor(access: ToolboxAccessResponse | null | undefined, project: string | null): ToolboxPersona {
  return personasOn(access, project).persona ?? DEFAULT_TOOLBOX_PERSONA;
}

/**
 * End a thread in the app now, so the case's digest, summary and report and
 * the worktrees are deleted as soon as the page moves on. Fire and forget: a
 * failure only leaves the thread to end on its own (idle, next investigation,
 * quit), and an app whose bridge lacks the call is left to do just that.
 */
export function endCodeThread(fogbugz: FogbugzToolsetBridge, threadId: string): void {
  const close = (fogbugz as Partial<FogbugzToolsetBridge>).closeCodeThread;
  if (typeof close !== "function") return;
  void close.call(fogbugz, threadId).catch(() => undefined);
}

// ---------------------------------------------------------------------------
// The section
// ---------------------------------------------------------------------------

export interface FogbugzCodeSectionProps {
  fogbugz: FogbugzToolsetBridge;
  caseId: number;
  /** The attachment's file name, as the digest names it. */
  fileName: string;
  /** The digest the summary was written from, exactly as reviewed. */
  digestMarkdown: string;
  /** The summary sections exactly as reviewed. */
  sections: string;
  /** True while the page runs something else on the shared lane (a digest, a summary, a post). */
  laneBusy: boolean;
  /** Told whenever a code run starts or ends, so the page disables Summarize while it runs. */
  onRunningChange: (running: boolean) => void;
}

interface Investigation {
  threadId: string;
  report: string;
  project: string;
  ref: FogbugzCodeRef;
  persona: ToolboxPersona;
}

interface FollowUp {
  question: string;
  answer: string;
}

const fieldClass =
  "rounded-[10px] border border-[#3F3F46] bg-[#111113] text-foreground outline-none transition-[border-color,box-shadow] focus:border-[#66A1FF] focus:ring-2 focus:ring-[#66A1FF]/25 disabled:opacity-60";

function CodeNotice({
  message,
  testId,
  children,
}: {
  message: FogbugzCodeMessage;
  testId: string;
  children?: ReactNode;
}) {
  return (
    <div
      role="alert"
      data-testid={testId}
      className="flex items-start gap-2.5 rounded-[10px] border border-red-500/40 bg-red-500/5 px-4 py-3 text-[13px] leading-relaxed"
    >
      <CircleAlert className="mt-0.5 h-4 w-4 shrink-0 text-red-400" aria-hidden="true" />
      <div className="flex flex-1 flex-col gap-2">
        <p>{message.text}</p>
        {message.setup && (
          <Link to="/toolbox/setup" className="text-link hover:underline">
            Open Setup checks
          </Link>
        )}
        {children}
      </div>
    </div>
  );
}

function Progress({ fallback, event }: { fallback: string; event: FogbugzCodeProgressEvent | null }) {
  return (
    <div className="flex flex-col gap-0.5" role="status" data-testid="fogbugz-code-progress">
      <span className="flex items-center gap-2.5 text-[13px] text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
        {event ? FOGBUGZ_CODE_RUN_PHASE_LABELS[event.phase] : fallback}
      </span>
      {event?.activity && <span className="pl-6 text-xs text-muted-foreground">{event.activity}</span>}
    </div>
  );
}

/**
 * The "Look into the code" section. Mounted by the page under a summary
 * review, keyed by case and attachment, so leaving the case or discarding
 * the summary unmounts it and drops its thread.
 */
export function FogbugzCodeSection({
  fogbugz,
  caseId,
  fileName,
  digestMarkdown,
  sections,
  laneBusy,
  onRunningChange,
}: FogbugzCodeSectionProps) {
  const { data: access } = useToolboxAccess();
  const adminProjectsQuery = useGroomingProjects({ enabled: access?.isAdmin === true });
  const projects = useMemo(
    () => codeProjectsOf(access, adminProjectsQuery.data?.map((p) => p.project)),
    [access, adminProjectsQuery.data],
  );
  const [chosenProject, setChosenProject] = useState<string | null>(null);
  const project = chosenProject ?? defaultCodeProject(projects, readLastProject());

  const refsQuery = useQuery<FogbugzCodeRefsResult>({
    queryKey: ["toolbox", "fogbugz", "code-refs", project?.toLowerCase() ?? ""],
    queryFn: () => fogbugz.listCodeRefs({ project: project ?? "" }),
    enabled: project !== null,
    retry: false,
    staleTime: 60_000,
    // Copies being updated (BUSY) is short-lived: ask again until the versions can be read.
    refetchInterval: (query) =>
      query.state.data?.ok === false && query.state.data.errorCode === "BUSY" ? CODE_REFS_BUSY_RETRY_MS : false,
  });
  const refs = refsQuery.data?.ok ? refsQuery.data : null;
  const refOptions = useMemo(() => (refs ? codeRefOptions(refs.refs, refs.defaultRef) : []), [refs]);
  const [chosenRefKey, setChosenRefKey] = useState<string | null>(null);
  const ref = refOptions.find((option) => codeRefKey(option) === chosenRefKey) ?? refOptions[0] ?? null;
  const notSetUp = refsQuery.data?.ok === false && refsQuery.data.errorCode === "CODE_NOT_SET_UP";
  const persona = codePersonaFor(access, project);

  const [running, setRunning] = useState<FogbugzCodeOperation | null>(null);
  const [progress, setProgress] = useState<FogbugzCodeProgressEvent | null>(null);
  const [investigation, setInvestigation] = useState<Investigation | null>(null);
  const [failure, setFailure] = useState<FogbugzCodeMessage | null>(null);
  const [followUps, setFollowUps] = useState<FollowUp[]>([]);
  const [askFailure, setAskFailure] = useState<FogbugzCodeMessage | null>(null);
  const [question, setQuestion] = useState("");
  const [copied, setCopied] = useState(false);
  // Each run gets a number; a result for an older number (project or version changed, section closed) is ignored.
  const runSeq = useRef(0);
  const reportClaudeRun = useClaudeRunOutcome();
  const runningRef = useRef<FogbugzCodeOperation | null>(null);
  runningRef.current = running;
  // The open thread's id, so it is ended when the section closes.
  const threadRef = useRef<string | null>(null);
  threadRef.current = investigation?.threadId ?? null;

  // Tell the page when the shared lane is taken, and free it when the section closes.
  useEffect(() => {
    onRunningChange(running !== null);
  }, [running, onRunningChange]);
  useEffect(
    () => () => {
      runSeq.current += 1;
      // A run still going when the section closes (Discard, another case) is stopped, and the thread ended.
      if (runningRef.current !== null) void fogbugz.cancel();
      if (threadRef.current !== null) endCodeThread(fogbugz, threadRef.current);
      onRunningChange(false);
    },
    [fogbugz, onRunningChange],
  );

  // Progress lines replace each other while a run is going; events that do not parse are ignored.
  useEffect(
    () =>
      fogbugz.onCodeProgress((event) => {
        const parsed = fogbugzCodeProgressEventSchema.safeParse(event);
        if (parsed.success && runningRef.current === parsed.data.operation) setProgress(parsed.data);
      }),
    [fogbugz],
  );

  /** Drop the report and end its thread (project or version changed, a new investigation). */
  const closeThread = () => {
    runSeq.current += 1;
    if (investigation) endCodeThread(fogbugz, investigation.threadId);
    setInvestigation(null);
    setFollowUps([]);
    setFailure(null);
    setAskFailure(null);
    setQuestion("");
    setCopied(false);
  };

  const investigate = async () => {
    if (running !== null || laneBusy || project === null || ref === null || notSetUp) return;
    closeThread();
    runSeq.current += 1;
    const seq = runSeq.current;
    setRunning("investigate");
    setProgress(null);
    const target = { project, ref, persona };
    let result: Awaited<ReturnType<FogbugzToolsetBridge["investigateCode"]>>;
    try {
      result = await fogbugz.investigateCode({ caseId, fileName, digestMarkdown, sections, ...target });
    } catch {
      result = { ok: false, errorCode: "UNKNOWN", message: "" };
    }
    reportClaudeRun(result);
    if (seq !== runSeq.current) return;
    setRunning(null);
    setProgress(null);
    if (!result.ok) {
      setFailure(fogbugzCodeMessage(result.errorCode, "investigate", result.message));
      return;
    }
    // The report is untrusted model output: the three headings, in order, or nothing shown.
    if (!hasAllCodeReportSections(result.report)) {
      setFailure(fogbugzCodeMessage("NO_SUMMARY_RETURNED", "investigate"));
      return;
    }
    setInvestigation({ threadId: result.threadId, report: result.report, ...target });
  };

  const asked = followUps.length;
  const limitReached = asked >= FOGBUGZ_CODE_MAX_FOLLOW_UPS;
  const trimmedQuestion = question.trim();
  const canAsk =
    investigation !== null &&
    running === null &&
    !laneBusy &&
    !limitReached &&
    askFailure?.restart !== true &&
    trimmedQuestion.length > 0 &&
    question.length <= FOGBUGZ_CODE_QUESTION_MAX_CHARS;

  const ask = async (event: FormEvent) => {
    event.preventDefault();
    if (!canAsk) return;
    const seq = runSeq.current;
    const text = trimmedQuestion;
    setRunning("ask");
    setProgress(null);
    setAskFailure(null);
    let result: Awaited<ReturnType<FogbugzToolsetBridge["askCode"]>>;
    try {
      result = await fogbugz.askCode({ threadId: investigation.threadId, question: text });
    } catch {
      result = { ok: false, errorCode: "UNKNOWN", message: "" };
    }
    reportClaudeRun(result);
    if (seq !== runSeq.current) return;
    setRunning(null);
    setProgress(null);
    if (!result.ok) {
      setAskFailure(fogbugzCodeMessage(result.errorCode, "ask", result.message));
      return;
    }
    setFollowUps((list) => [...list, { question: text, answer: result.answer }]);
    setQuestion("");
  };

  const copyReport = () => {
    if (!investigation) return;
    // Absent outside a secure context, whatever the DOM types say.
    const clipboard = navigator.clipboard as Clipboard | undefined;
    if (!clipboard) return;
    void clipboard.writeText(investigation.report).then(
      () => {
        setCopied(true);
      },
      () => {
        setCopied(false);
      },
    );
  };

  const cancel = () => {
    // The running call resolves with CANCELED, which shows its own message.
    if (running !== null) void fogbugz.cancel();
  };

  const pickersDisabled = running !== null;
  const lookDisabled = running !== null || laneBusy || project === null || ref === null || notSetUp;

  return (
    <section
      className="glass-card flex flex-col gap-3 rounded-[10px] px-[18px] py-4"
      aria-labelledby="fogbugz-code"
      data-testid="fogbugz-code"
    >
      <div className="flex flex-col gap-1">
        <h2 id="fogbugz-code" className="font-display text-base font-semibold">
          Look into the code
        </h2>
        <p className="text-[13px] text-muted-foreground">
          Your Claude reads this project's code on this computer and explains where the error comes from. Nothing here
          is posted to the case.
        </p>
      </div>

      {access && projects.length === 0 && !adminProjectsQuery.isPending && (
        <p className="text-[13px] text-muted-foreground" data-testid="fogbugz-code-no-project">
          You have no Toolbox project whose code Claude could read. Ask an Admin or your Project Manager for a role.
        </p>
      )}

      {project !== null && (
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground" htmlFor="fogbugz-code-project">
            Project
            <select
              id="fogbugz-code-project"
              className={cn(fieldClass, "h-9 px-3 text-sm font-medium")}
              value={project}
              disabled={pickersDisabled}
              onChange={(event) => {
                closeThread();
                setChosenProject(event.target.value);
                setChosenRefKey(null);
              }}
            >
              {projects.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground" htmlFor="fogbugz-code-ref">
            Version
            <select
              id="fogbugz-code-ref"
              className={cn(fieldClass, "h-9 min-w-[10rem] px-3 text-sm font-medium")}
              value={ref ? codeRefKey(ref) : ""}
              disabled={pickersDisabled || ref === null}
              onChange={(event) => {
                closeThread();
                setChosenRefKey(event.target.value);
              }}
            >
              {ref === null && <option value="">{refsQuery.isPending ? "Loading versions…" : "No version"}</option>}
              {refOptions.map((option, index) => (
                <option key={codeRefKey(option)} value={codeRefKey(option)}>
                  {index === 0 ? `${codeRefWords(option)} (default)` : codeRefWords(option)}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {project !== null && ref !== null && (
        <p className="text-[13px]" data-testid="fogbugz-code-target">
          Claude will read <span className="font-medium">{project}</span> at{" "}
          <span className="font-medium">{codeRefWords(ref)}</span> and write for you as a{" "}
          {TOOLBOX_PERSONA_LABELS[persona]}.
        </p>
      )}

      {notSetUp && (
        <p className="flex flex-wrap gap-x-2 text-[13px] text-muted-foreground" data-testid="fogbugz-code-not-set-up">
          <span>This project's code isn't set up on this computer.</span>
          <Link to="/toolbox/setup" className="text-link hover:underline">
            Open Setup checks
          </Link>
        </p>
      )}

      {refsQuery.data?.ok === false && !notSetUp && (
        <CodeNotice
          testId="fogbugz-code-refs-error"
          message={
            refsQuery.data.errorCode === "BUSY"
              ? { text: CODE_REFS_BUSY_WORDS, setup: false, restart: false }
              : fogbugzCodeMessage(refsQuery.data.errorCode, "investigate", refsQuery.data.message)
          }
        />
      )}

      {refsQuery.isError && (
        <p className="text-[13px] text-amber-400" role="alert" data-testid="fogbugz-code-refs-error">
          The versions of this project's code could not be read.
        </p>
      )}

      <div className="flex items-center gap-2">
        {running === "investigate" ? (
          <>
            <Progress fallback="Starting Claude" event={progress} />
            <Button type="button" variant="outline" size="sm" onClick={cancel}>
              Cancel
            </Button>
          </>
        ) : (
          <Button
            type="button"
            variant="outline"
            disabled={lookDisabled}
            onClick={() => {
              void investigate();
            }}
          >
            {investigation || failure?.restart || askFailure?.restart ? "Look into the code again" : "Look into the code"}
          </Button>
        )}
      </div>

      {failure && <CodeNotice testId="fogbugz-code-error" message={failure} />}

      {investigation && (
        <div className="flex flex-col gap-3" data-testid="fogbugz-code-report">
          <div className="flex flex-wrap items-center gap-2">
            <Chip tone="cur">Claude's reading of the code — verify before acting</Chip>
            <span className="text-xs text-muted-foreground" data-testid="fogbugz-code-studied">
              Studied {investigation.project} at {codeRefWords(investigation.ref)}, written for a{" "}
              {TOOLBOX_PERSONA_LABELS[investigation.persona]}
            </span>
            <span className="flex-1" />
            <Button type="button" variant="ghost" size="sm" onClick={copyReport}>
              <Copy className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
              {copied ? "Copied" : "Copy report"}
            </Button>
          </div>
          <div
            className="max-h-[60vh] overflow-y-auto rounded-md border border-white/[0.08] bg-white/[0.02] p-4 text-sm"
            data-testid="fogbugz-code-report-body"
          >
            <MarkdownRenderer content={investigation.report} disableLinks />
          </div>

          {followUps.length > 0 && (
            <ol className="flex flex-col gap-3" data-testid="fogbugz-code-thread">
              {followUps.map((entry, index) => (
                <li key={index} className="flex flex-col gap-1.5" data-testid="fogbugz-code-follow-up">
                  <p className="self-end rounded-xl border border-[#1F1F23] bg-[#111113] px-3.5 py-2 text-[13px]">
                    {entry.question}
                  </p>
                  <div className="rounded-md border border-white/[0.08] bg-white/[0.02] px-4 py-3 text-sm">
                    <MarkdownRenderer content={entry.answer} disableLinks />
                  </div>
                </li>
              ))}
            </ol>
          )}

          {running === "ask" && (
            <div className="flex items-center gap-2">
              <Progress fallback="Answering" event={progress} />
              <Button type="button" variant="outline" size="sm" onClick={cancel}>
                Cancel
              </Button>
            </div>
          )}

          {askFailure && <CodeNotice testId="fogbugz-code-ask-error" message={askFailure} />}

          {limitReached && !askFailure && (
            <CodeNotice testId="fogbugz-code-limit" message={fogbugzCodeMessage("TOO_MANY_FOLLOW_UPS", "ask")} />
          )}

          <form className="flex flex-col gap-1.5" onSubmit={(event) => void ask(event)} data-testid="fogbugz-code-ask">
            <label htmlFor="fogbugz-code-question" className="text-xs font-medium text-muted-foreground">
              Ask a follow-up question
            </label>
            <textarea
              id="fogbugz-code-question"
              rows={2}
              maxLength={FOGBUGZ_CODE_QUESTION_MAX_CHARS}
              className={cn(fieldClass, "w-full resize-y px-3 py-2 text-sm")}
              value={question}
              disabled={running !== null || limitReached || askFailure?.restart === true}
              onChange={(event) => {
                setQuestion(event.target.value);
              }}
            />
            <div className="flex items-center gap-3">
              <Button type="submit" size="sm" disabled={!canAsk}>
                Ask
              </Button>
              <span className="text-xs text-muted-foreground" data-testid="fogbugz-code-count">
                {asked} of {FOGBUGZ_CODE_MAX_FOLLOW_UPS} follow-ups
              </span>
              <span className="text-xs text-muted-foreground">
                {question.length.toLocaleString()} / {FOGBUGZ_CODE_QUESTION_MAX_CHARS.toLocaleString()}
              </span>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}
